import { action, computed, makeObservable, observable } from 'mobx';

import { Store } from '../../store/store';
import { Message, StoreNode } from '../../store';
import Routes, { BackRoutes } from '../../routes';
import { UploadTask } from '../../services/upload';
import { Team } from '../../entities/team';
import { DocumentUploadFileState } from './documentUploadFileState';
import { WarrantUploadState } from './warrantUploadState';
import {
  GuidedTrainingDomSync,
  getGuidedTrainingCoachPosition,
  getGuidedTrainingStorageKey,
  getGuidedTrainingTargetRect,
  guidedTrainingTargetSelector,
  type GuidedTrainingCoachPosition,
  type GuidedTrainingRect,
} from '../../components/guidedTraining/guidedTrainingCore';

export type DocumentUploadWorkflow = 'warrant' | 'translation';

type DocumentUploadGuidedTrainingRect = GuidedTrainingRect;
type CoachPosition = GuidedTrainingCoachPosition;

type Phase = 'upload' | 'processing' | 'document';

type Step = {
  title: string;
  description: string;
  targetId?: string;
  primaryLabel: string;
};

type Progress = {
  phase: Phase;
  currentStepIndex: number;
  workflow: DocumentUploadWorkflow | null;
  teamId: string | null;
  jobId: string | null;
  taskId: string | null;
};

type UploadPageContext = {
  getTeamId: () => string | null;
  getWorkflow: () => DocumentUploadWorkflow;
  setWorkflow: (workflow: DocumentUploadWorkflow) => void;
  getTranslationState: () => DocumentUploadFileState;
  getWarrantState: () => WarrantUploadState;
};

const progressStoragePrefix = 'guidedTraining.documentUpload';
const activeBodyClass = 'document-upload-guided-training-active';
const positionRetryDelayMs = 50;
const positionRetryAttempts = 14;
const normalizeIndustry = (industry?: string | null) =>
  (industry || '').replace(/[\s_-]/g, '').toLowerCase();

const getTargetRect = (targetId?: string): DocumentUploadGuidedTrainingRect | null =>
  getGuidedTrainingTargetRect(targetId);

const getCoachPosition = (rect: DocumentUploadGuidedTrainingRect | null): CoachPosition =>
  getGuidedTrainingCoachPosition(rect, {
    maxWidth: 720,
    height: 200,
    defaultMaxWidth: 420,
    defaultLeft: 'right',
    defaultTopRatio: 0.68,
    defaultTopMin: 120,
  });

export class DocumentUploadGuidedTrainingState
  extends StoreNode {

  readonly nodeType = 'DocumentUploadGuidedTraining';

  constructor(store: Store) {
    super(store);
    makeObservable(this);
    this.receive(this.receiver, ['UploadService:taskCompleted']);
    this.domSync = new GuidedTrainingDomSync({
      syncPosition: this.syncPosition,
      activeBodyClass,
      retryAttempts: positionRetryAttempts,
      retryDelayMs: positionRetryDelayMs,
    });
  }

  @observable isArmed = false;
  @observable isVisible = false;
  @observable phase: Phase = 'upload';
  @observable currentStepIndex = 0;
  @observable workflow: DocumentUploadWorkflow | null = null;
  @observable teamId: string | null = null;
  @observable jobId: string | null = null;
  @observable taskId: string | null = null;
  @observable.ref targetRect: DocumentUploadGuidedTrainingRect | null = null;
  @observable.ref coachPosition: CoachPosition = { top: 120, left: 16, width: 720 };

  private uploadPageContext: UploadPageContext | null = null;
  private readonly domSync: GuidedTrainingDomSync;

  @computed
  get steps(): Step[] {
    const workflow = this.workflow;
    const isWarrant = workflow === 'warrant';
    const canUseWarrant = this.canUseWarrantWorkflow;

    return [
      {
        title: canUseWarrant ? 'Choose the intended result' : 'Choose Translate Document',
        description: canUseWarrant
          ? 'Use Warrant Affidavit Draft to combine appropriate law-enforcement source documents. Use Translate Document when the goal is a translated copy.'
          : 'Use Translate Document to upload a document and create a translated copy.',
        targetId: 'document-upload-workflow',
        primaryLabel: canUseWarrant ? 'Choose above' : 'Next',
      },
      {
        title: isWarrant ? 'Upload warrant source documents' : 'Upload a document',
        description: isWarrant
          ? 'Confirm the named Library, then drag and drop reports, narratives, or notes, or click Select Files.'
          : 'Confirm the named Library, then drag and drop a file or click Select Files. Both methods use the same secure upload flow.',
        targetId: 'document-upload-file-dropzone',
        primaryLabel: 'Select a file',
      },
      {
        title: isWarrant ? 'Confirm document roles' : 'Choose the language pair',
        description: isWarrant
          ? 'Confirm the primary narrative and supplemental documents before starting the draft.'
          : 'Confirm the document language and choose the target language. CLIPr will preserve the original and create a separate translated version.',
        targetId: 'document-upload-settings',
        primaryLabel: isWarrant ? 'Start draft' : 'Start translation',
      },
      {
        title: isWarrant ? 'Warrant draft ready' : 'Translation ready',
        description: isWarrant
          ? 'Review the generated draft, make edits, then download it when ready.'
          : 'The original and translated documents remain separate. Review the translated text, make any edits, then download either version.',
        targetId: 'document-training-result',
        primaryLabel: canUseWarrant ? 'Try the other workflow' : 'Finish',
      },
    ];
  }

  @computed
  get currentStep(): Step | null {
    return this.steps[this.currentStepIndex] || null;
  }

  @computed
  get totalSteps(): number {
    return this.steps.length;
  }

  @computed
  get allowTargetInteraction(): boolean {
    return this.phase !== 'processing';
  }

  @computed
  get showBackButton(): boolean {
    return this.currentStepIndex > 0;
  }

  @computed
  get showFinishButton(): boolean {
    return this.phase === 'document'
      && this.currentStepIndex === this.steps.length - 1
      && this.canUseWarrantWorkflow;
  }

  @computed
  get trackedTask(): UploadTask | null {
    if (!this.taskId) return null;
    return this.store.uploadService.tasks.find(task => task.id === this.taskId) || null;
  }

  @computed
  get canUseWarrantWorkflow(): boolean {
    return this.isPublicSafetyContext();
  }

  private get progressStorageKey(): string {
    return getGuidedTrainingStorageKey(this.store, progressStoragePrefix);
  }

  private get completedStorageKey(): string {
    return getGuidedTrainingStorageKey(this.store, progressStoragePrefix, 'completed');
  }

  get hasCompleted(): boolean {
    return !!this.store.storage.getLocal(this.completedStorageKey, true);
  }

  getDefaultWorkflow(team?: Team | null): DocumentUploadWorkflow {
    return this.isPublicSafetyContext(team) ? 'warrant' : 'translation';
  }

  registerUploadPage(context: UploadPageContext) {
    this.uploadPageContext = context;
  }

  unregisterUploadPage(context: UploadPageContext) {
    if (this.uploadPageContext === context)
      this.uploadPageContext = null;
  }

  @action
  handleUploadPageMounted(): boolean {
    const restoredProgress = !this.isArmed && this.restoreProgress();

    if (!this.isArmed && !restoredProgress && !this.hasCompleted)
      this.open();

    if (!this.isArmed)
      return false;

    if (this.phase === 'document') {
      this.goToDocumentPage();
      return true;
    }

    if (this.phase === 'processing' && this.trackedTask)
      return true;

    if (this.phase === 'processing' && !this.trackedTask) {
      this.phase = 'upload';
      this.currentStepIndex = 1;
      this.taskId = null;
      this.saveProgress();
    }

    this.applyWorkflowToPage();

    if (this.phase === 'upload' && this.currentStepIndex > 1 && !this.hasSelectedDocumentFile()) {
      this.currentStepIndex = 1;
      this.saveProgress();
    }

    this.isVisible = true;
    this.domSync.bindListeners();
    this.domSync.schedulePositionSync(true);

    return true;
  }

  @action
  handleUploadPageUnmounted() {
    if (!this.isArmed) return;
    if (this.phase === 'processing') {
      this.pause();
      return;
    }
    if (this.phase === 'document') return;

    this.pause();
  }

  @action
  handleDocumentPageMounted(jobId: string | null): boolean {
    const restoredProgress = !this.isArmed && this.restoreProgress();
    if (!this.isArmed && !restoredProgress)
      return false;

    if (!this.canContinueOnDocument(jobId)) {
      this.clearProgress();
      this.resetRuntime();
      return false;
    }

    return true;
  }

  @action
  handleDocumentReady(jobId: string | null): boolean {
    const restoredProgress = !this.isArmed && this.restoreProgress();
    if (!this.isArmed && !restoredProgress)
      return false;

    if (!this.canContinueOnDocument(jobId)) {
      this.clearProgress();
      this.resetRuntime();
      return false;
    }

    this.currentStepIndex = 3;
    this.isVisible = true;
    this.domSync.bindListeners();
    this.saveProgress();
    this.domSync.schedulePositionSync(true);

    return true;
  }

  @action
  handleDocumentPageUnmounted() {
    if (!this.isArmed || this.phase !== 'document') return;
    this.pause();
  }

  @action
  handleWorkflowSelected(workflow: DocumentUploadWorkflow) {
    if (!this.isArmed || this.phase !== 'upload') return;

    this.workflow = workflow;
    this.teamId = this.uploadPageContext?.getTeamId() ?? this.teamId;

    if (this.currentStepIndex === 0)
      this.currentStepIndex = 1;

    this.saveProgress();
    this.domSync.schedulePositionSync(true);
  }

  @action
  handleDocumentFileSelected(workflow: DocumentUploadWorkflow) {
    if (!this.isArmed || this.phase !== 'upload') return;
    if (this.currentStepIndex < 1 || this.currentStepIndex > 2) return;

    this.workflow = this.workflow || workflow;
    this.currentStepIndex = 2;
    this.saveProgress();
    this.domSync.schedulePositionSync(true);
  }

  @action
  handleWorkflowUploadStarted(workflow: DocumentUploadWorkflow, taskId: string | null, teamId: string | null) {
    const restoredProgress = !this.isArmed && this.restoreProgress();
    if (!this.isArmed && !restoredProgress)
      return;

    this.workflow = workflow;
    this.teamId = teamId;
    this.taskId = taskId;
    this.phase = 'processing';
    this.currentStepIndex = 3;
    this.isVisible = false;
    this.domSync.unbindListeners();
    this.domSync.cancelPositionSync();
    this.saveProgress();
  }

  @action
  handleWorkflowDocumentStarted(workflow: DocumentUploadWorkflow, jobId: string | null, teamId: string | null) {
    const restoredProgress = !this.isArmed && this.restoreProgress();
    if (!this.isArmed && !restoredProgress)
      return;

    this.workflow = workflow;
    this.teamId = teamId;
    this.jobId = jobId;
    this.taskId = null;
    this.phase = 'document';
    this.currentStepIndex = 3;
    this.isVisible = false;
    this.domSync.unbindListeners();
    this.domSync.cancelPositionSync();
    this.saveProgress();
  }

  @action
  next = () => {
    if (this.phase === 'document' || this.currentStepIndex === 3) {
      if (this.canUseWarrantWorkflow)
        this.tryOtherWorkflow();
      else
        this.complete();
      return;
    }

    switch (this.currentStepIndex) {
      case 0:
        this.workflow = this.workflow || this.uploadPageContext?.getWorkflow() || this.getDefaultWorkflow();
        if (!this.workflow) {
          this.domSync.schedulePositionSync(true);
          return;
        }
        this.currentStepIndex = 1;
        break;
      case 1:
        if (!this.hasSelectedDocumentFile()) {
          this.openFilePicker();
          return;
        }
        this.currentStepIndex = 2;
        break;
      case 2:
        void this.submitCurrentWorkflow();
        return;
      default:
        break;
    }

    this.saveProgress();
    this.domSync.schedulePositionSync(true);
  }

  @action
  finish = () => {
    if (!this.showFinishButton) return;
    this.complete();
  }

  @action
  skip = () => {
    this.complete();
  }

  @action
  back = () => {
    if (!this.showBackButton) return;

    if (this.phase === 'document') {
      this.phase = 'upload';
      this.currentStepIndex = 2;
      this.goToUploadPage();
      this.saveProgress();
      return;
    }

    this.currentStepIndex = Math.max(0, this.currentStepIndex - 1);
    this.saveProgress();
    this.domSync.schedulePositionSync(true);
  }

  @action
  private tryOtherWorkflow() {
    const workflow: DocumentUploadWorkflow = this.workflow === 'translation' ? 'warrant' : 'translation';
    this.workflow = workflow;
    this.phase = 'upload';
    this.currentStepIndex = 1;
    this.jobId = null;
    this.taskId = null;
    this.isVisible = false;
    this.domSync.unbindListeners();
    this.domSync.cancelPositionSync();
    this.saveProgress();
    this.goToUploadPage();
  }

  @action
  syncPosition = () => {
    const rect = getTargetRect(this.currentStep?.targetId);
    this.targetRect = rect;
    this.coachPosition = getCoachPosition(rect);

    if (!rect && this.currentStep?.targetId && this.domSync.canRetry)
      this.domSync.schedulePositionRetry();
  }

  @action
  private open() {
    this.clearProgress();
    this.resetRuntime();
    this.isArmed = true;
    this.isVisible = true;
    this.phase = 'upload';
    this.currentStepIndex = 0;
    this.teamId = this.uploadPageContext?.getTeamId() ?? null;
    this.workflow = this.getDefaultWorkflow();
    this.saveProgress();
  }

  @action
  pause = () => {
    if (this.isArmed)
      this.saveProgress();

    this.isVisible = false;
    this.targetRect = null;
    this.domSync.unbindListeners();
    this.domSync.cancelPositionSync();
  }

  @action
  private complete() {
    this.store.storage.setLocal(this.completedStorageKey, true, true);
    this.clearProgress();
    this.resetRuntime();
  }

  @action
  private resetRuntime() {
    this.isArmed = false;
    this.isVisible = false;
    this.phase = 'upload';
    this.currentStepIndex = 0;
    this.workflow = null;
    this.teamId = null;
    this.jobId = null;
    this.taskId = null;
    this.targetRect = null;
    this.domSync.unbindListeners();
    this.domSync.cancelPositionSync();
  }

  private hasSelectedDocumentFile(): boolean {
    const workflow = this.workflow || this.uploadPageContext?.getWorkflow();
    if (workflow === 'translation')
      return (this.uploadPageContext?.getTranslationState().stagedItems.length ?? 0) > 0;
    if (workflow === 'warrant')
      return (this.uploadPageContext?.getWarrantState().stagedItems.length ?? 0) > 0;

    return false;
  }

  private openFilePicker() {
    const input = document.querySelector<HTMLInputElement>(
      `${guidedTrainingTargetSelector('document-upload-file-dropzone')} input[type="file"]`
    );
    input?.click();
  }

  @action
  private async submitCurrentWorkflow() {
    const context = this.uploadPageContext;
    const workflow = this.workflow || context?.getWorkflow();
    if (!context || !workflow) {
      this.domSync.schedulePositionSync(true);
      return;
    }

    this.workflow = workflow;
    this.teamId = context.getTeamId();

    if (workflow === 'translation') {
      const tasks = await context.getTranslationState().submit();
      const task = tasks?.[0];
      if (!task) {
        this.phase = 'upload';
        this.currentStepIndex = 2;
        this.isVisible = true;
        this.domSync.bindListeners();
        this.saveProgress();
        this.domSync.schedulePositionSync(true);
        return;
      }

      return;
    }

    const jobId = await context.getWarrantState().submit();
    if (!jobId) {
      this.phase = 'upload';
      this.currentStepIndex = 2;
      this.isVisible = true;
      this.domSync.bindListeners();
      this.saveProgress();
      this.domSync.schedulePositionSync(true);
      return;
    }

    this.handleWorkflowDocumentStarted('warrant', jobId, this.teamId);
  }

  private applyWorkflowToPage() {
    if (!this.workflow || !this.uploadPageContext) return;
    this.uploadPageContext.setWorkflow(this.workflow);
  }

  private isPublicSafetyContext(team?: Team | null): boolean {
    const userIndustry = normalizeIndustry(this.store.user?.industry);
    if (userIndustry === 'corporate')
      return false;
    if (userIndustry === 'publicsafety')
      return true;

    const targetTeam = team ?? this.currentTeam();
    return !!targetTeam?.publicSafetyActivated || normalizeIndustry(targetTeam?.industry) === 'publicsafety';
  }

  private currentTeam(): Team | null {
    const teamId = this.teamId || this.uploadPageContext?.getTeamId();
    return this.store.teamManager.getTeam(teamId);
  }

  private goToUploadPage() {
    const teamId = this.teamId || this.uploadPageContext?.getTeamId() || undefined;
    const route = teamId
      ? BackRoutes[Routes.teamDocumentUpload()].route({ currentPath: this.store.history.location.pathname, teamId })
      : BackRoutes[Routes.documentUpload()].route({ currentPath: this.store.history.location.pathname });
    this.store.goTo(route);
  }

  private goToDocumentPage() {
    if (!this.jobId) return;

    const route = BackRoutes[Routes.userDocument()].route({
      id: this.jobId,
      teamId: this.teamId || undefined,
      currentPath: this.store.history.location.pathname,
    });
    this.store.goTo(route);
  }

  private canContinueOnDocument(jobId: string | null): boolean {
    if (this.phase !== 'document')
      return false;

    if (!this.jobId)
      return false;

    return !jobId || this.jobId === jobId;
  }

  private saveProgress() {
    this.store.storage.setLocal(this.progressStorageKey, {
      phase: this.phase,
      currentStepIndex: this.currentStepIndex,
      workflow: this.workflow,
      teamId: this.teamId,
      jobId: this.jobId,
      taskId: this.taskId,
    }, true);
  }

  private loadProgress(): Progress | null {
    return this.store.storage.getLocal<Progress>(this.progressStorageKey, true);
  }

  private clearProgress() {
    this.store.storage.removeLocal(this.progressStorageKey);
  }

  @action
  private restoreProgress(): boolean {
    const progress = this.loadProgress();
    if (!progress)
      return false;

    this.phase = progress.phase === 'document'
      ? 'document'
      : progress.phase === 'processing' ? 'processing' : 'upload';
    this.workflow = progress.workflow === 'translation' || progress.workflow === 'warrant'
      ? progress.workflow
      : null;
    this.teamId = progress.teamId;
    this.jobId = progress.jobId;
    this.taskId = progress.taskId;
    this.currentStepIndex = clamp(progress.currentStepIndex, 0, this.steps.length - 1);
    this.isArmed = true;
    this.saveProgress();

    return true;
  }

  private receiver = (msg: Message) => {
    if (msg.type !== 'taskCompleted') return;
    if (!this.isArmed || this.phase !== 'processing' || this.workflow !== 'translation') return;
    if (!this.trackedTask?.isCompleted) return;

    const jobId = msg.payload?.job?.id;
    if (!jobId) return;

    this.jobId = jobId;
    this.teamId = msg.payload?.teamId || this.teamId;
    this.phase = 'document';
    this.currentStepIndex = 3;
    this.saveProgress();
    this.goToDocumentPage();
  }
}
